import { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { IPaginationResponse } from '../interfaces/responces/IPagination.response';
import { IClient, IStockOwnerFilter } from '../interfaces/IClient';
import { InviteStatus } from '../interfaces/IInviteStatus';
import { IInvitation } from '../interfaces/IInvitation';
import { IStockOwnerDashboardData } from '../Components/Pages/Main/MantisIntegration/StockOwnerDashboard/types';
import { normalizeStockOwnerListInviteStatus } from '../helpers/stock-owner-invite-status';

export interface IStockOwner {
  id: string;
  name?: string; // Company name from stock_owners table
  firstName: string;
  lastName: string;
  contact?: string; // Contact person full name (firstName + lastName)
  responsible?: string; // Contact person from stock_owners table
  phone: string;
  role: 'client' | 'stock_owner';
  avatar: string | null;
  status: InviteStatus;
  statusId?: number;
  invitation?: IInvitation | null;
  /** stock_owners.is_active — see ADR-009 */
  isActive?: boolean;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface IInviteStockOwnerData {
  firstName: string;
  lastName: string;
  phone: string;
  expiredAt?: string;
  language?: string; // e.g., 'en', 'he'
}

export interface IStockOwnersListResponse {
  list: IStockOwner[];
  total: number;
  totalPages: number;
}

export interface IStockOwnersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isUseInvitedByUserId?: boolean;
}

export interface IUpdateStockOwnerLogisticsPayload {
  id: number;
  defaultWarehouseId?: number | null;
  deliveryDays?: number | null;
}

export const stockOwnersApi = createApi({
  reducerPath: 'stockOwnersApi',
  tagTypes: ['StockOwner'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    getStockOwnerList: build.query<
      IPaginationResponse<IClient[]>,
      IStockOwnerFilter
    >({
      transformResponse: (response: { data: IPaginationResponse<IClient[]> }) => {
        const data = response.data;
        if (!data?.list?.length) return data;
        return { ...data, list: normalizeStockOwnerListInviteStatus(data.list as any[]) };
      },
      query: (params) => ({
        url: '/stock-owners/list',
        method: 'GET',
        params,
      }),
      providesTags: () => ['StockOwner'],
    }),
    getStockOwnerById: build.query<IClient, { id: number }>({
      transformResponse: (response: { data: IClient }) => response.data,
      query: ({ id }) => ({
        url: `/stock-owners/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'StockOwner', id }],
    }),
    updateStockOwnerLogistics: build.mutation<
      IClient,
      IUpdateStockOwnerLogisticsPayload
    >({
      transformResponse: (response: { data: IClient }) => response.data,
      query: ({ id, defaultWarehouseId, deliveryDays }) => ({
        url: `/stock-owners/${id}/logistics`,
        method: 'PATCH',
        body: { defaultWarehouseId, deliveryDays },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'StockOwner', id },
        'StockOwner',
      ],
    }),
    // Management endpoints for Settings page
    getStockOwners: build.query<IStockOwnersListResponse, IStockOwnersQueryParams | void>({
      transformResponse: (response: { data: IStockOwnersListResponse }) => {
        const data = response.data;
        if (!data?.list?.length) return data;
        return { ...data, list: normalizeStockOwnerListInviteStatus(data.list as any[]) };
      },
      query: (params: any = {}) => {
        const queryParams = new URLSearchParams();
        
        params.page && queryParams.append('page', String(params.page));
        params.limit && queryParams.append('limit', String(params.limit));
        params.search && queryParams.append('search', params.search);
        params.isUseInvitedByUserId && queryParams.append('isUseInvitedByUserId', String(params.isUseInvitedByUserId));
        
        return {
          url: `/stock-owners/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['StockOwner'],
    }),
    // DEPRECATED: Use invitationsApi instead
    // inviteStockOwner: Use invitationsApi.createInvitation
    // resendInvite: Use invitationsApi.resendInvitation
    // cancelInvitation: Use invitationsApi.revokeInvitation
    deactivateStockOwner: build.mutation<void, string>({
      query: (stockOwnerId) => ({
        url: `/stock-owners/${stockOwnerId}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['StockOwner'],
    }),
    activateStockOwner: build.mutation<void, string>({
      query: (stockOwnerId) => ({
        url: `/stock-owners/${stockOwnerId}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: ['StockOwner'],
    }),
    getStockOwnerDashboardStatistics: build.query<IStockOwnerDashboardData, void>({
      query: () => ({
        url: '/stock-owners/dashboard/statistics',
        method: 'GET',
      }),
      transformResponse: (response: { data: IStockOwnerDashboardData }) => response.data,
    }),
  }),
});

export const {
  useGetStockOwnerListQuery,
  useGetStockOwnerByIdQuery,
  useUpdateStockOwnerLogisticsMutation,
  useGetStockOwnersQuery,
  useLazyGetStockOwnersQuery,
  // DEPRECATED: Use invitationsApi hooks instead
  // useInviteStockOwnerMutation -> useCreateInvitationMutation
  // useResendInviteMutation -> useResendInvitationMutation
  // useCancelInvitationMutation -> useRevokeInvitationMutation
  useDeactivateStockOwnerMutation,
  useActivateStockOwnerMutation,
  useGetStockOwnerDashboardStatisticsQuery,
} = stockOwnersApi;

export default stockOwnersApi;
